import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Mail, Calendar, MapPin, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useEvents } from '@/hooks/useEvents';
import { EventWithStatus } from '@/types/event';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EventInvitationButtonProps {
  memberId: string;
  memberName: string;
  size?: 'sm' | 'default';
  variant?: 'default' | 'outline' | 'ghost';
  className?: string;
}

export const EventInvitationButton: React.FC<EventInvitationButtonProps> = ({
  memberId,
  memberName,
  size = 'sm',
  variant = 'outline',
  className = ''
}) => {
  const { events } = useEvents();
  const [isLoading, setIsLoading] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState<EventWithStatus[]>([]);

  useEffect(() => {
    // Filter upcoming events only
    const upcoming = events.filter(event => event.status === 'upcoming');
    setUpcomingEvents(upcoming);
  }, [events]);

  const handleSendInvitation = async (event: EventWithStatus) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-targeted-event-invitation', {
        body: { 
          eventId: event.id,
          memberId: memberId,
          memberName: memberName
        },
      });

      if (error) throw error;

      toast.success('Invitation envoyée', {
        description: `${memberName} a été invité(e) à ${event.title}`
      });
    } catch (error) {
      console.error('Error sending targeted invitation:', error);
      toast.error('Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setIsLoading(false);
    }
  };

  // If no upcoming events, don't show the button
  if (upcomingEvents.length === 0) {
    return null;
  }

  // If only one event, show direct button
  if (upcomingEvents.length === 1) {
    const event = upcomingEvents[0];
    return (
      <Button 
        variant={variant} 
        size={size}
        onClick={() => handleSendInvitation(event)}
        disabled={isLoading}
        className={className}
      >
        <Mail className="h-4 w-4 mr-2" />
        Inviter à l'événement
      </Button>
    );
  }

  // If multiple events, show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          disabled={isLoading}
          className={className}
        >
          <Mail className="h-4 w-4 mr-2" />
          Inviter à un événement
          <Badge variant="secondary" className="ml-2">
            {upcomingEvents.length}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {upcomingEvents.map((event) => (
          <DropdownMenuItem
            key={event.id}
            onClick={() => handleSendInvitation(event)}
            className="flex flex-col items-start p-3 cursor-pointer"
          >
            <div className="flex items-center gap-2 w-full">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">{event.title}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              <MapPin className="h-3 w-3" />
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="h-3 w-3" />
              <span>{new Date(event.start_date).toLocaleDateString('fr-FR')}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};